import { Database, Monitor, TestTube2, Wifi } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AdminOnlyEmptyState } from '@/components/auth/admin-only-empty-state';
import { useIsAdmin } from '@/components/auth/useIsAdmin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { type ScreenMode, screenModeConfig } from '@/config/screen-mode-config';
import { useScreenModes } from '@/providers/ScreenModesProvider';

interface ScreenEntry {
  key: string;
  label: string;
  configMode: ScreenMode;
}

interface ScreenGroup {
  name: string;
  description: string;
  screens: ScreenEntry[];
}

function buildScreenGroups(): ScreenGroup[] {
  const entry = (key: string, label: string): ScreenEntry => ({
    key,
    label,
    configMode: screenModeConfig[key] ?? 'demo',
  });

  return [
    {
      name: 'Main',
      description: 'Configure data modes for main screens',
      screens: [
        entry('Homepage', 'Homepage'),
        entry('Customers', 'Customers'),
        entry('Transactions', 'Transactions'),
        entry('Credit_Scoring', 'Credit Scoring'),
        entry('My_Tasks', 'My Tasks'),
      ],
    },
    {
      name: 'Payments',
      description: 'Configure data modes for payment screens',
      screens: [
        entry('Payments', 'Payments'),
        entry('Payments_Receipts', 'Receipts'),
        entry('Payments_Allocate', 'Allocate'),
      ],
    },
    {
      name: 'Collections',
      description: 'Configure data modes for collection screens',
      screens: [
        entry('Collections_Dashboard', 'Dashboard'),
        entry('Collections_Collections_Queue', 'Collections Queue'),
        entry('Collections_Promise_to_Pay', 'Promise to Pay'),
      ],
    },
    {
      name: 'Dispute Management',
      description: 'Configure data modes for dispute screens',
      screens: [
        entry('Dispute_Management_Dashboard', 'Dashboard'),
        entry('Dispute_Management_All_Disputes', 'All Disputes'),
      ],
    },
    {
      name: 'Analytics & Reporting',
      description: 'Configure data modes for analytics screens',
      screens: [
        entry('Analytics_Reporting_Ageing_Analysis', 'Ageing Analysis'),
        entry(
          'Analytics_Reporting_Multi_Company_Dashboard',
          'Multi-Company Dashboard',
        ),
        entry('Analytics_Reporting_Emailing', 'Emailing'),
      ],
    },
    {
      name: 'Settings',
      description: 'Configure data modes for settings screens',
      screens: [
        entry('Settings_Company', 'Company'),
        entry('Settings_General', 'General'),
        entry('Settings_Users', 'Users'),
        entry('Settings_Templates', 'Templates'),
        entry('Settings_Collection_Strategies', 'Collection Strategies'),
        entry('Settings_Dispute_Types', 'Dispute Types'),
        entry('Settings_Integrations', 'Integrations'),
        entry('Settings_AI_Configuration', 'AI Configuration'),
      ],
    },
  ];
}

const screenGroups = buildScreenGroups();

// Count screens by config mode
const totalScreens = Object.keys(screenModeConfig).length;
const toggleableScreens = Object.values(screenModeConfig).filter(
  (m) => m === 'all',
).length;
const demoOnlyScreens = totalScreens - toggleableScreens;

export function ScreenDataModesPage() {
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const {
    getScreenMode,
    setScreenMode,
    setAllToDemo,
    setAllToLive,
    canUseDemoData,
  } = useScreenModes();

  if (isAdminLoading) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <output
          aria-label="Loading"
          className="block h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground"
        />
      </div>
    );
  }
  if (!isAdmin) return <AdminOnlyEmptyState />;

  if (!canUseDemoData) {
    return <Navigate to="/settings/company" replace />;
  }

  // Count how many toggleable screens are currently set to each mode
  const liveCount = Object.entries(screenModeConfig)
    .filter(([, mode]) => mode === 'all')
    .filter(([key]) => getScreenMode(key) === 'live').length;
  const demoToggleableCount = toggleableScreens - liveCount;

  const handleSetAllToDemo = () => {
    setAllToDemo();
    toast.info('All toggleable screens set to Demo mode');
  };

  const handleSetAllToLive = () => {
    setAllToLive();
    toast.info('All toggleable screens set to Live mode');
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="container max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Monitor className="h-6 w-6 text-primary" />
            <h1 className="text-3xl font-bold tracking-tight">
              Screen Data Modes
            </h1>
          </div>
          <p className="text-muted-foreground">
            Configure whether each screen uses demo data or live API data
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    Total Screens
                  </p>
                  <p className="text-2xl font-bold">{totalScreens}</p>
                </div>
                <div className="rounded-full bg-primary/10 p-3">
                  <Monitor className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    Using Demo Data
                  </p>
                  <p className="text-2xl font-bold">
                    {demoOnlyScreens + demoToggleableCount}
                  </p>
                </div>
                <div className="rounded-full bg-amber-500/10 p-3">
                  <TestTube2 className="h-6 w-6 text-amber-600 dark:text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    Using Live Data
                  </p>
                  <p className="text-2xl font-bold">{liveCount}</p>
                </div>
                <div className="rounded-full bg-green-500/10 p-3">
                  <Database className="h-6 w-6 text-green-600 dark:text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>
              Apply data mode settings to all screens at once
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleSetAllToDemo}
                className="gap-2"
              >
                <TestTube2 className="h-4 w-4" />
                Set All to Demo
              </Button>
              <Button
                variant="outline"
                onClick={handleSetAllToLive}
                className="gap-2"
              >
                <Wifi className="h-4 w-4" />
                Set All to Live
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Screen Groups */}
        {screenGroups.map((group) => (
          <Card key={group.name}>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CardTitle>{group.name}</CardTitle>
                <Badge variant="secondary">
                  {group.screens.length} screens
                </Badge>
              </div>
              <CardDescription>{group.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {group.screens.map((screen) => {
                  const currentMode = getScreenMode(screen.key);
                  const isToggleable = screen.configMode === 'all';
                  const isLive = currentMode === 'live';

                  return (
                    <div
                      key={screen.key}
                      className="flex items-center justify-between py-3 px-4 rounded-lg border bg-amber-50 dark:bg-amber-950/20 hover:bg-amber-100/80 dark:hover:bg-amber-950/30 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium">
                          {screen.label}
                        </span>
                        {isToggleable ? (
                          <Badge
                            variant="outline"
                            className={
                              isLive
                                ? 'border-green-500/50 bg-green-500/10 text-green-700 dark:text-green-400'
                                : 'border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400'
                            }
                          >
                            {isLive ? (
                              <>
                                <Database className="h-3 w-3 mr-1" />
                                Live Data
                              </>
                            ) : (
                              <>
                                <TestTube2 className="h-3 w-3 mr-1" />
                                Demo Data
                              </>
                            )}
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="border-muted-foreground/30 text-muted-foreground"
                          >
                            <TestTube2 className="h-3 w-3 mr-1" />
                            Demo Only
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">
                          Demo
                        </span>
                        <Switch
                          checked={isLive}
                          onCheckedChange={(checked) =>
                            setScreenMode(screen.key, checked ? 'live' : 'demo')
                          }
                          disabled={!isToggleable}
                        />
                        <span className="text-xs text-muted-foreground">
                          Live
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
